import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';

/**
 * Is this app the build the server is serving?
 *
 * Diagnosis first, and a fix second. An installed home screen app can keep
 * running a bundle from weeks ago — the service worker now checks for updates,
 * but that failed silently once already, and when it does there is nothing on
 * anyone's screen saying which version they're on. A member reports "the thing
 * you shipped isn't there" and nobody can tell whether that's a bug or a stale
 * install.
 *
 * The bundle filename is the version: CRA content-hashes it, so if the server's
 * differs from the one this page loaded, this page is out of date. Full stop —
 * no guessing.
 *
 * Checked on load and whenever the app comes back to the foreground, which for
 * a PWA is when a deploy would otherwise go unnoticed.
 */

/** The hashed bundle this page is actually running. */
export function ownBundle() {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;                       // dev server: no hashed bundle
  return (el.getAttribute('src') || '').split('/').pop();
}

export function useVersion() {
  const [info, setInfo] = useState({ bundle: null, startedAt: null });
  const mine = ownBundle();

  const check = useCallback(async () => {
    try {
      const r = await API.get('/version');
      setInfo({ bundle: r.data?.bundle || null, startedAt: r.data?.startedAt || null });
    } catch (e) { /* offline is not a version problem */ }
  }, []);

  useEffect(() => {
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  // Only a real mismatch counts. In development neither side has a hashed
  // bundle, and a missing answer means the request failed, not that the app is
  // stale — nagging in either case would train people to ignore this.
  const comparable = !!(mine && info.bundle &&
    /^main\.[a-z0-9]+\.js$/.test(mine) && /^main\.[a-z0-9]+\.js$/.test(info.bundle));
  return {
    mine,
    serverBundle: info.bundle,
    startedAt: info.startedAt,
    stale: comparable && mine !== info.bundle,
    refresh: check
  };
}

/** Just the hash, for display: main.710c4e81.js → 710c4e81 */
export function shortHash(bundle) {
  if (!bundle) return null;
  const m = /^main\.([a-z0-9]+)\.js$/.exec(bundle);
  return m ? m[1] : bundle;
}

export default function VersionCheck() {
  const { stale } = useVersion();
  if (!stale) return null;

  return (
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 78, zIndex: 1500,
      background: 'linear-gradient(135deg,#013369,#0a5299)',
      border: '1px solid rgba(127,179,255,0.45)', borderRadius: 12,
      padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 8px 28px rgba(0,0,0,0.45)'
    }}>
      <span style={{ fontSize: 20 }}>🔄</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>A newer version is ready</div>
        <div style={{ fontSize: 12, color: '#bcd', marginTop: 2 }}>
          You're running an older copy of the app.
        </div>
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#D50A0A',
                 color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                 fontFamily: "'Oswald',sans-serif", letterSpacing: 0.5 }}
      >RELOAD</button>
    </div>
  );
}
